import React from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: December 24, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to 444-ai ("we," "our," or "us"). This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you use our AI-powered services and applications. 
            This policy complies with the Information Technology Act, 2000, and the Information Technology 
            (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul>
            <li>Name, email address, and contact details</li>
            <li>Username and password</li>
            <li>Payment and billing information</li>
            <li>Device information and IP address</li>
            <li>Usage data and interaction with our AI services</li>
          </ul>

          <h3>2.2 Sensitive Personal Data</h3>
          <p>
            We may collect sensitive personal data as defined under Indian law, including:
          </p>
          <ul>
            <li>Financial information (bank account, credit/debit card details)</li>
            <li>Passwords and authentication credentials</li>
            <li>Biometric information (if applicable)</li>
          </ul>
          <p>
            Collection of such information will be with your explicit consent and only when necessary 
            for providing our services.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information for:</p>
          <ul>
            <li>Providing and improving our AI services</li>
            <li>Personalizing user experience</li>
            <li>Processing transactions and payments</li>
            <li>Communicating with you about services, updates, and promotions</li>
            <li>Training and improving our AI models</li>
            <li>Complying with legal obligations</li>
            <li>Preventing fraud and ensuring security</li>
          </ul>
        </section>

        <section>
          <h2>4. Legal Basis for Processing (Indian Law Compliance)</h2>
          <p>We process your personal information based on:</p>
          <ul>
            <li>Your explicit consent</li>
            <li>Performance of a contract with you</li>
            <li>Compliance with legal obligations under Indian law</li>
            <li>Our legitimate business interests</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Third-party vendors who assist in providing our services</li>
            <li><strong>Business Partners:</strong> With your consent for collaborative services</li>
            <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
          </ul>
          <p>
            We do not sell your personal information to third parties. Any data shared with third parties 
            is subject to confidentiality agreements and data protection requirements.
          </p>
        </section>

        <section>
          <h2>6. Data Security</h2>
          <p>
            We implement reasonable security practices and procedures as required under the IT Act, 2000 
            and IT Rules, 2011, including:
          </p>
          <ul>
            <li>Encryption of sensitive data in transit and at rest</li>
            <li>Regular security audits and assessments</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Employee training on data protection</li>
            <li>Incident response procedures</li>
          </ul>
          <p>
            However, no method of transmission over the Internet is 100% secure, and we cannot guarantee 
            absolute security.
          </p>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes 
            outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce 
            our agreements. When data is no longer needed, we will securely delete or anonymize it.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>Under Indian law, you have the following rights:</p>
          <ul>
            <li><strong>Right to Access:</strong> Request access to your personal information</li>
            <li><strong>Right to Correction:</strong> Request correction of inaccurate information</li>
            <li><strong>Right to Withdrawal:</strong> Withdraw consent at any time (where applicable)</li>
            <li><strong>Right to Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Right to Opt-Out:</strong> Opt-out of marketing communications</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information provided below.
          </p>
        </section>

        <section>
          <h2>9. AI-Specific Data Processing</h2>
          <p>
            Our AI services may process your data to:
          </p>
          <ul>
            <li>Generate personalized responses and recommendations</li>
            <li>Improve model accuracy and performance</li>
            <li>Analyze usage patterns and trends</li>
          </ul>
          <p>
            We are committed to ethical AI practices and ensure that AI processing is transparent, 
            fair, and respects your privacy.
          </p>
        </section>

        <section>
          <h2>10. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to enhance user experience, analyze usage, 
            and deliver personalized content. You can control cookie preferences through your browser settings.
          </p>
        </section>

        <section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our services are not intended for children under the age of 18. We do not knowingly collect 
            personal information from children. If you believe we have inadvertently collected such information, 
            please contact us immediately.
          </p>
        </section>

        <section>
          <h2>12. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than India. We ensure 
            that such transfers comply with applicable data protection laws and that adequate safeguards 
            are in place.
          </p>
        </section>

        <section>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes 
            by posting the new policy on this page and updating the "Last Updated" date. We encourage you 
            to review this policy periodically.
          </p>
        </section>

        <section>
          <h2>14. Grievance Officer</h2>
          <p>
            In accordance with the Information Technology Act, 2000 and rules made thereunder, we have 
            appointed a Grievance Officer to address your concerns regarding data protection:
          </p>
          <div className="contact-info">
            <p><strong>Name:</strong> [Grievance Officer Name]</p>
            <p><strong>Email:</strong> grievance@444-ai.com</p>
            <p><strong>Response Time:</strong> Within 30 days of receipt of complaint</p>
          </div>
        </section>

        <section>
          <h2>15. Contact Us</h2>
          <p>
            If you have any questions or concerns about this Privacy Policy or our data practices, 
            please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>444-ai</strong></p>
            <p><strong>Email:</strong> privacy@444-ai.com</p>
            <p><strong>Address:</strong> [Your Business Address]</p>
          </div>
        </section>

        <section>
          <h2>16. Governing Law</h2>
          <p>
            This Privacy Policy is governed by and construed in accordance with the laws of India. 
            Any disputes arising out of or related to this policy shall be subject to the exclusive 
            jurisdiction of the courts in [Your City], India.
          </p>
        </section>

        <div className="acknowledgment">
          <p>
            By using our services, you acknowledge that you have read, understood, and agree to be bound 
            by this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
